import { doAppGraphqlRequest } from 'ducks/requests'
import { selectCurrentTicketId } from 'selectors/location'

import {
  selectCurrentContactId,
  selectCurrentContactIdOrCustomerId,
} from 'ducks/crm/contacts'
import {
  selectCurrentContactIsLoading,
  selectPageIsLoading,
  selectPageRequestKey,
  selectPageRequestVariables,
} from '../selectors'
import {
  contactsNormalizationSchema,
  conversationContactNormalizationSchema,
} from '../schema'
import {
  FETCH_CONTACTS,
  FETCH_CONVERSATION_CONTACT_PAGE,
  FETCH_CONVERSATION_CONTACT_STARTED,
  FETCH_CONVERSATION_CONTACT_SUCCESS,
} from '../types'

export const customFieldValueNodeQuery = `
  nodes {
    id
    key
    value {
      ... on Text {
        content
      }
      ... on Address {
        street
        street2
        city
        country
        postalCode
      }
      ... on Link {
        text
        link
      }
    }
  }
`

const contactCustomFieldValuesSubQuery = `
  customFieldValues(first: 20, after: $contactAfter) {
    pageInfo {
      hasNextPage
      endCursor
    }
    ${customFieldValueNodeQuery}
  }
`

const companyCustomFieldValuesSubQuery = `
  customFieldValues(first: 20, after: $companyAfter) {
    pageInfo {
      hasNextPage
      endCursor
    }
    ${customFieldValueNodeQuery}
  }
`

const fetchContactsQuery = `
  query Contacts(
    $first: Int,
    $after: String,
    $filter: ContactFilter,
    $orderBy: ContactOrder
  ) {
    contacts(first: $first, after: $after, filter: $filter, orderBy: $orderBy) {
      totalCount
      totalPageCount
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      nodes {
        id
        contactType
        conversationCount
        createdAt
        labels {
          edges {
            node {
              id
              name
            }
          }
        }
        ${contactCustomFieldValuesSubQuery}
        companies {
          nodes {
            id
            createdAt
            ${companyCustomFieldValuesSubQuery}
          }
        }
      }
    }
  }
`

export function doFetchContacts() {
  return (dispatch, getState) => {
    const state = getState()
    const isLoading = selectPageIsLoading(state)
    const requestKey = selectPageRequestKey(state)
    const variables = selectPageRequestVariables(state)

    if (isLoading) return null

    return dispatch(
      doAppGraphqlRequest(FETCH_CONTACTS, fetchContactsQuery, variables, {
        normalizationSchema: contactsNormalizationSchema,
        meta: {
          requestKey,
        },
      })
    )
  }
}

const fetchConversationContactCustomFieldsFirstPageQuery = `
  query ConversationContact($companyAfter: String, $contactAfter: String, $number: Int!
  ) {
    conversation(number: $number) {
      number
      contact {
        id
        avatarUrl
        companies {
          nodes {
            id
            name
            domain
            ${companyCustomFieldValuesSubQuery}
          }
        }
        ${contactCustomFieldValuesSubQuery}
        email
        firstName
        lastName
      }
    }
  }
`

const fetchConversationContactCustomFieldsQuery = `
  query ConversationContact($companyAfter: String, $contactAfter: String, $number: Int!
  ) {
    conversation(number: $number) {
      number
      contact {
        id
        companies {
          nodes {
            id
            ${companyCustomFieldValuesSubQuery}
          }
        }
        ${contactCustomFieldValuesSubQuery}
      }
    }
  }
`

export function doFetchCurrentConversationContact() {
  return async (dispatch, getState) => {
    const state = getState()
    const id = selectCurrentTicketId(state)
    const customerId = selectCurrentContactIdOrCustomerId(state)
    const isLoading = selectCurrentContactIsLoading(state)
    let contactId = selectCurrentContactId(state)

    if (!customerId) return null
    if (isLoading) return null
    let contactHasNextPage
    let companyHasNextPage
    let contactAfter
    let companyAfter
    let firstLoop = true

    dispatch({
      type: FETCH_CONVERSATION_CONTACT_STARTED,
      entities: {
        contacts: contactId
          ? { [contactId]: { id: contactId, isLoading: true } }
          : {},
      },
      meta: { mergeEntities: true },
    })
    do {
      const variables = {
        companyAfter,
        contactAfter,
        number: parseInt(id, 10),
      }
      // eslint-disable-next-line no-await-in-loop
      const result = await dispatch(
        doAppGraphqlRequest(
          FETCH_CONVERSATION_CONTACT_PAGE,
          firstLoop
            ? fetchConversationContactCustomFieldsFirstPageQuery
            : fetchConversationContactCustomFieldsQuery,
          variables,
          {
            meta: { mergeEntities: true },
            normalizationSchema: conversationContactNormalizationSchema,
          }
        )
      )
      const {
        conversation: {
          contact,
          contact: { companies, customFieldValues: { pageInfo } = {} } = {},
        } = {},
      } = result
      const {
        nodes: [
          { customFieldValues: { pageInfo: companyPageInfo = {} } = {} } = {},
        ] = [],
      } =
        companies || {}
      contactId = contact.id
      contactHasNextPage = pageInfo.hasNextPage
      companyHasNextPage = companyPageInfo.hasNextPage
      contactAfter = pageInfo.endCursor
      companyAfter = companyPageInfo.endCursor
      firstLoop = false
    } while (contactHasNextPage || companyHasNextPage)

    dispatch({
      type: FETCH_CONVERSATION_CONTACT_SUCCESS,
      entities: {
        contacts: contactId
          ? { [contactId]: { id: contactId, isLoaded: true, isLoading: false } }
          : {},
      },
      meta: { mergeEntities: true },
    })
    return null
  }
}
